import type { RemediationVariantTask, Task } from "@history-sim/content";

export type EvaluableTask = Task | RemediationVariantTask;

export type TaskResponse =
  | { type: "single_choice"; optionId: string }
  | { type: "drag_sort"; orderedIds: string[] }
  | { type: "drag_match"; pairs: Record<string, string> };

export interface EvaluationResult {
  valid: boolean;
  correct: boolean;
  score: number;
  feedback: string;
  misconceptionCode?: string;
  diagnostic?: EvaluationDiagnostic;
}

export interface EvaluationDiagnostic {
  code: "ORDER_ADJACENCY_INVERTED";
  message: string;
  itemIds: [string, string];
}

function sameOrder(actual: readonly string[], expected: readonly string[]): boolean {
  return (
    actual.length === expected.length && actual.every((value, index) => value === expected[index])
  );
}

function hasExactMembers(actual: readonly string[], expected: readonly string[]): boolean {
  if (actual.length !== expected.length || new Set(actual).size !== actual.length) return false;
  const expectedIds = new Set(expected);
  return actual.every((value) => expectedIds.has(value));
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((entry) => typeof entry === "string")
  );
}

function buildOrderDiagnostic(
  actual: readonly string[],
  expected: readonly string[],
  labels: ReadonlyMap<string, string>,
): EvaluationDiagnostic | undefined {
  const actualPositions = new Map(actual.map((id, index) => [id, index]));
  for (let index = 0; index < expected.length - 1; index += 1) {
    const beforeId = expected[index];
    const afterId = expected[index + 1];
    if (!beforeId || !afterId) continue;
    const beforePosition = actualPositions.get(beforeId);
    const afterPosition = actualPositions.get(afterId);
    if (
      beforePosition === undefined ||
      afterPosition === undefined ||
      beforePosition < afterPosition
    ) {
      continue;
    }

    const beforeLabel = labels.get(beforeId) ?? beforeId;
    const afterLabel = labels.get(afterId) ?? afterId;
    return {
      code: "ORDER_ADJACENCY_INVERTED",
      itemIds: [beforeId, afterId],
      message: `具体倒置：你把“${afterLabel}”排在了“${beforeLabel}”之前；正确的相邻关系是“${beforeLabel} → ${afterLabel}”。`,
    };
  }
  return undefined;
}

export function evaluateTask(task: EvaluableTask, response: TaskResponse): EvaluationResult {
  const invalidType = (): EvaluationResult => ({
    valid: false,
    correct: false,
    score: 0,
    feedback: "作答数据与任务类型不匹配，请重新操作。",
  });

  switch (task.type) {
    case "single_choice": {
      if (response?.type !== "single_choice") return invalidType();
      const option = task.options.find((item) => item.id === response.optionId);
      if (!option) {
        return {
          valid: false,
          correct: false,
          score: 0,
          feedback: "请选择一个有效选项后再提交。",
        };
      }
      const correct = option.id === task.correctOptionId;
      return {
        valid: true,
        correct,
        score: correct ? 1 : 0,
        feedback: option.feedback,
        ...(option.misconceptionCode ? { misconceptionCode: option.misconceptionCode } : {}),
      };
    }
    case "drag_sort": {
      if (response?.type !== "drag_sort") return invalidType();
      const itemIds = task.items.map((item) => item.id);
      if (
        !Array.isArray(response.orderedIds) ||
        !response.orderedIds.every((id) => typeof id === "string") ||
        !hasExactMembers(response.orderedIds, itemIds)
      ) {
        return {
          valid: false,
          correct: false,
          score: 0,
          feedback: "排序结果不完整或包含无效项目，请保留全部项目后重新提交。",
        };
      }
      const correct = sameOrder(response.orderedIds, task.correctOrder);
      const directedFeedback =
        !correct && "errorFeedback" in task
          ? task.errorFeedback.find((item) => sameOrder(item.whenOrder, response.orderedIds))
          : undefined;
      const diagnostic = correct
        ? undefined
        : buildOrderDiagnostic(
            response.orderedIds,
            task.correctOrder,
            new Map(task.items.map((item) => [item.id, item.text])),
          );
      return {
        valid: true,
        correct,
        score: correct ? 1 : 0,
        feedback: correct
          ? task.explanation
          : (directedFeedback?.feedback ??
            `${task.hint}${diagnostic ? ` ${diagnostic.message}` : ""} 你可以使用拖动、键盘或上下移按钮重新排序。`),
        ...(diagnostic ? { diagnostic } : {}),
        ...(directedFeedback
          ? { misconceptionCode: directedFeedback.misconceptionCode }
          : diagnostic
            ? { misconceptionCode: diagnostic.code }
            : {}),
      };
    }
    case "drag_match": {
      if (response?.type !== "drag_match" || !isStringRecord(response.pairs)) {
        return invalidType();
      }
      const keys = Object.keys(task.correctPairs);
      const expectedRightIds = new Set(task.right.map((item) => item.id));
      const responseKeys = Object.keys(response.pairs);
      const responseRightIds = responseKeys.map((key) => response.pairs[key] ?? "");
      const correctPairsAreOneToOne =
        new Set(Object.values(task.correctPairs)).size === keys.length;
      const valid =
        hasExactMembers(responseKeys, keys) &&
        responseRightIds.every((rightId) => expectedRightIds.has(rightId)) &&
        (!correctPairsAreOneToOne || new Set(responseRightIds).size === responseRightIds.length);
      if (!valid) {
        return {
          valid: false,
          correct: false,
          score: 0,
          feedback: "配对结果不完整或包含无效项目，请完成每一组配对后重新提交。",
        };
      }
      const correct =
        keys.length === responseKeys.length &&
        keys.every((key) => response.pairs[key] === task.correctPairs[key]);
      return {
        valid: true,
        correct,
        score: correct ? 1 : 0,
        feedback: correct ? task.explanation : `${task.hint} 检查每组概念与说明之间的关系。`,
      };
    }
  }
}
